import { useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import {
  Plus, Edit2, Trash2, RefreshCw, CheckCircle, XCircle, Search, RotateCcw, Clock, Send
} from "lucide-react";

const getRoleBadgeColor = (role) => {
  switch (role) {
    case "super_admin": return "bg-red-100 text-red-800 border-red-200";
    case "admin": return "bg-blue-100 text-blue-800 border-blue-200";
    case "content_manager": return "bg-purple-100 text-purple-800 border-purple-200";
    case "support_staff": return "bg-green-100 text-green-800 border-green-200";
    case "analyst": return "bg-amber-100 text-amber-800 border-amber-200";
    default: return "bg-indigo-100 text-indigo-800 border-indigo-200";
  }
};

const formatDate = (dateStr) => {
  if (!dateStr) return "N/A";
  return new Date(dateStr).toLocaleString("en-IN", {
    dateStyle: "medium",
    timeStyle: "short"
  });
};

const PROTECTED_EMAIL = "admin@contentiumtech.com";

export default function AdminsTab({
  admins,
  roles = [],
  onCreateAdmin,
  onEditAdmin,
  onDeactivateAdmin,
  onReactivateAdmin,
  onResendInvite,
  onReset2FA,
  onChangeRole
}) {
  const [query, setQuery] = useState("");
  const [roleFilter, setRoleFilter] = useState("");

  const roleLabel = (value) => {
    const r = roles.find((x) => x.value === value);
    return r ? r.name : (value || "").replace(/_/g, " ").toUpperCase();
  };

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return (admins || []).filter((a) => {
      if (roleFilter && a.role !== roleFilter) return false;
      if (!q) return true;
      return (
        (a.name || "").toLowerCase().includes(q) ||
        (a.email || "").toLowerCase().includes(q) ||
        (a.role || "").toLowerCase().includes(q) ||
        roleLabel(a.role).toLowerCase().includes(q)
      );
    });
  }, [admins, query, roleFilter, roles]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="space-y-4" data-testid="admins-tab-content">
      <div className="flex justify-between items-center">
        <h2 className="text-xl font-semibold text-gray-900">Users</h2>
        <Button
          onClick={onCreateAdmin}
          className="bg-red-500 hover:bg-red-600 text-white"
          data-testid="create-admin-btn"
        >
          <Plus className="h-4 w-4 mr-2" />
          Create User
        </Button>
      </div>

      {/* Search & filter */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <Input
            placeholder="Search by name, email or role…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            className="pl-9 bg-white border-gray-200"
            data-testid="users-search-input"
          />
        </div>
        <select
          value={roleFilter}
          onChange={(e) => setRoleFilter(e.target.value)}
          className="h-10 rounded-md border border-gray-200 bg-white px-3 text-sm text-gray-700"
          data-testid="users-role-filter"
        >
          <option value="">All roles</option>
          {roles.map((r) => (
            <option key={r.value} value={r.value}>{r.name}</option>
          ))}
        </select>
      </div>

      <Card className="bg-white border-gray-200 shadow-sm">
        <CardContent className="p-0 overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr className="border-b border-gray-200">
                <th className="text-left p-4 text-gray-600 font-medium">User</th>
                <th className="text-left p-4 text-gray-600 font-medium">Role</th>
                <th className="text-left p-4 text-gray-600 font-medium">Status</th>
                <th className="text-left p-4 text-gray-600 font-medium">Last Login</th>
                <th className="text-left p-4 text-gray-600 font-medium">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((admin) => {
                const isProtected = admin.email === PROTECTED_EMAIL;
                return (
                  <tr key={admin.id} className="border-b border-gray-100 hover:bg-gray-50">
                    <td className="p-4">
                      <div>
                        <p className="font-medium text-gray-900">{admin.name}</p>
                        <p className="text-sm text-gray-500">{admin.email}</p>
                      </div>
                    </td>
                    <td className="p-4">
                      {onChangeRole && !isProtected ? (
                        <select
                          value={admin.role}
                          onChange={(e) => {
                            if (e.target.value !== admin.role) onChangeRole(admin, e.target.value);
                          }}
                          className="h-8 rounded-md border border-gray-200 bg-white px-2 text-sm text-gray-800"
                          data-testid={`inline-role-${admin.id}`}
                        >
                          {roles.map((r) => (
                            <option key={r.value} value={r.value}>
                              {r.name}{r.is_system_role ? "" : " (Custom)"}
                            </option>
                          ))}
                        </select>
                      ) : (
                        <Badge className={`${getRoleBadgeColor(admin.role)} border`}>
                          {roleLabel(admin.role)}
                        </Badge>
                      )}
                    </td>
                    <td className="p-4">
                      {admin.pending_invite && !admin.active ? (
                        <span className="flex items-center gap-1 text-indigo-600" data-testid={`pending-invite-${admin.id}`}>
                          <Clock className="h-4 w-4" /> Pending invite
                        </span>
                      ) : admin.active ? (
                        <span className="flex items-center gap-1 text-emerald-600">
                          <CheckCircle className="h-4 w-4" /> Active
                        </span>
                      ) : (
                        <span className="flex items-center gap-1 text-red-500">
                          <XCircle className="h-4 w-4" /> Inactive
                        </span>
                      )}
                    </td>
                    <td className="p-4 text-gray-500 text-sm">
                      {formatDate(admin.last_login)}
                    </td>
                    <td className="p-4">
                      <div className="flex gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => onEditAdmin(admin)}
                          className="text-gray-500 hover:text-blue-600 hover:bg-blue-50"
                          data-testid={`edit-admin-${admin.id}`}
                        >
                          <Edit2 className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => onReset2FA(admin)}
                          className="text-gray-500 hover:text-amber-600 hover:bg-amber-50"
                          title="Reset 2FA"
                          data-testid={`reset-2fa-${admin.id}`}
                        >
                          <RefreshCw className="h-4 w-4" />
                        </Button>
                        {admin.pending_invite && !admin.active && onResendInvite && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => onResendInvite(admin)}
                            className="text-gray-500 hover:text-indigo-600 hover:bg-indigo-50"
                            title="Resend invitation"
                            data-testid={`resend-invite-${admin.id}`}
                          >
                            <Send className="h-4 w-4" />
                          </Button>
                        )}
                        {!isProtected && admin.active && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => onDeactivateAdmin(admin)}
                            className="text-gray-500 hover:text-red-600 hover:bg-red-50"
                            title="Deactivate user"
                            data-testid={`deactivate-admin-${admin.id}`}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        )}
                        {!isProtected && !admin.active && !admin.pending_invite && onReactivateAdmin && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => onReactivateAdmin(admin)}
                            className="text-gray-500 hover:text-emerald-600 hover:bg-emerald-50"
                            title="Reactivate user"
                            data-testid={`reactivate-admin-${admin.id}`}
                          >
                            <RotateCcw className="h-4 w-4" />
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
              {filtered.length === 0 && (
                <tr>
                  <td colSpan={5} className="p-8 text-center text-gray-500">
                    No users found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </CardContent>
      </Card>
    </div>
  );
}
